import type { KinnserSyncStatus } from '../../services/api/types.gen';

/**
 * What Kinnser last sent for each clinic, read off the stored summary.
 *
 * A sync fills a clinic's identity only where the column is blank and never
 * replaces a value already there, so once an operator has typed something the
 * two can differ for good. Without this the screen could not say what the
 * difference was -- only that a sync had run.
 *
 * The summary rather than the payload: the payload is megabytes of rosters,
 * and these eleven fields are already lifted out of it at fetch time.
 */

/** Kinnser's value for one clinic, keyed by the column the screen shows. */
export type PulledIdentity = Record<string, unknown>;

export function pulledByClinicKey(
  status: KinnserSyncStatus
): Map<string, PulledIdentity> {
  const summary = status.summary as
    { clinic_list?: unknown } | null | undefined;
  const list = Array.isArray(summary?.clinic_list) ? summary.clinic_list : [];
  const byKey = new Map<string, PulledIdentity>();
  for (const entry of list) {
    if (!entry || typeof entry !== 'object') continue;
    const clinic = entry as PulledIdentity;
    const key = clinic.clinic_id;
    if (typeof key === 'string' && key !== '') byKey.set(key, clinic);
  }
  return byKey;
}

/**
 * Kinnser's value for one field, when it differs from what Roger holds.
 *
 * Null when they agree, when Kinnser sent nothing, or when the clinic was
 * never in a pull -- in all three there is nothing a second line would add,
 * and a caption under every cell would bury the ones that mean something.
 *
 * Compared as trimmed text, because these arrive as scraped strings and a
 * stray space is not a difference anybody wants pointed out.
 */
export function differingKinnserValue(
  pulled: PulledIdentity | undefined,
  field: string,
  stored: unknown
): string | null {
  if (!pulled) return null;
  const theirs = pulled[field];
  if (theirs === null || theirs === undefined) return null;
  const theirText = String(theirs).trim();
  if (theirText === '') return null;
  const ourText =
    stored === null || stored === undefined ? '' : String(stored).trim();
  return theirText.toLowerCase() === ourText.toLowerCase() ? null : theirText;
}
